import { View, Text, TouchableOpacity, ScrollView, Pressable } from 'react-native'
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { navigation as navigationObject } from '../../lib/Navigation/Navigation.component'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { useResponsive } from '@hooks'
import { useSessionStore } from '../../lib/Store/sessionStore.component'
import { AccueilProps } from './Accueil.types'
import styles from './Accueil.styles'
import MenuContextuel from './MenuContextuel'
import { ActualiteApi } from '../../api/Actualite'
import dayjs from 'dayjs'
import TilesList from '../../components/TilesList/TilesList'
import { TileType } from '../../components/TilesList/TilesList.type'
import { Dialog } from '../../components/Dialog'
import { useNotify } from '../../components/Notification'
import { enableBiometricLogin, getBiometricAvailability, getBiometryLabel, useBiometricEnrollmentStore } from '../../lib/Biometrics'

export const Accueil: FunctionComponent<AccueilProps> = ({ navigation }: any) => {
  const { getCurrentInfos } = useSessionStore()
  const { currentLogin, currentEntite } = getCurrentInfos()
  const isResponsive = useResponsive()

  const [menuVisible, setMenuVisible] = useState(false)
  const [hasActualites, setHasActualites] = useState(false)
  const notify = useNotify()

  const pendingBiometricCredentials = useBiometricEnrollmentStore((state) => state.pendingCredentials)
  const clearPendingBiometricCredentials = useBiometricEnrollmentStore((state) => state.clearPendingCredentials)
  const [biometricDialogVisible, setBiometricDialogVisible] = useState(false)
  const [biometricLabel, setBiometricLabel] = useState('la biométrie')
  const [biometricActivating, setBiometricActivating] = useState(false)

  useEffect(() => {
    if (!pendingBiometricCredentials) return
    getBiometricAvailability().then(({ biometryType }) => setBiometricLabel(getBiometryLabel(biometryType)))
    setBiometricDialogVisible(true)
  }, [pendingBiometricCredentials])

  const dismissBiometricProposal = useCallback(() => {
    setBiometricDialogVisible(false)
    clearPendingBiometricCredentials()
  }, [clearPendingBiometricCredentials])

  const handleEnableBiometric = useCallback(async () => {
    if (!pendingBiometricCredentials) return
    setBiometricActivating(true)
    try {
      const activated = await enableBiometricLogin(pendingBiometricCredentials.username, pendingBiometricCredentials.password)
      notify?.[activated ? 'success' : 'error'](
        activated ? 'Connexion biométrique activée' : 'Impossible d\'activer la connexion biométrique'
      )
    } finally {
      setBiometricActivating(false)
      dismissBiometricProposal()
    }
  }, [pendingBiometricCredentials, notify, dismissBiometricProposal])

const shortcuts = useMemo<TileType[]>(
  () => {
    const baseShortcuts: TileType[] = [
      {
        id: 2,
        title: 'Mes droits',
        subtitle: 'Soldes & compteurs',
        route: 'Droits',
        icon: 'shield-check-outline',
        color: '#10B981',
        background: '#ECFDF5',
      },
    ]

    // On ajoute "Actualités" seulement si hasActualites
    if (hasActualites) {
      baseShortcuts.unshift({
        id: 1,
        title: 'Actualités',
        subtitle: 'Consulter les dernières infos',
        route: 'Actualite',
        icon: 'newspaper-variant-outline',
        color: '#F97316',
        background: '#FFF7ED',
      })
    }
    if(process.env.NODE_ENV === 'development'){
      baseShortcuts.push({
        id: 3,
        title: 'Espace valideur',
        subtitle: 'Demandes à traiter',
        route: 'EspaceValideur',
        icon: 'calendar-check-outline',
        color: '#10B981',
        background: '#ECFDF5',
      })
    }
    return baseShortcuts
  },
  [hasActualites]
)

  const toggleModal = useCallback(() => setMenuVisible((oldValue) => !oldValue), [])

  const handlePress = useCallback(
    ({route}: {route: string}) => {
      console.log(route)
      if (route === 'NouvelleDemande' || route === 'EspaceValideur') {
        navigationObject.navigate(route, navigation)
        return
      }

      if (['Profil', 'Droits','Actualite' ].includes(route)) {
        navigationObject.navigate('HomeTabs', navigation, {
          screen: 'Autres',
          params: { screen: route },
        })
        return
      }
      navigationObject.navigate('HomeTabs', navigation, { screen: route })
    },
    [navigation]
  )

  useEffect(() => {
    let mounted = true

    const fetchActualites = async () => {
      try {
        const actualites = await ActualiteApi.loadActualite(
          `?dateRef=${dayjs().format('YYYY-MM-DD')}`
        )

        if (mounted) {
          setHasActualites(Array.isArray(actualites) && actualites.length > 0)
        }
      } catch (error) {
        console.warn('[Accueil] Erreur lors de la vérification des actualités:', error)
        if (mounted) setHasActualites(false)
      }
    }

    fetchActualites()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!isResponsive) {
      navigationObject.navigate('Actualite', navigation)
    }
  }, [isResponsive, navigation])

  if (!isResponsive) {
    return null
  }

  const getInitials = (name?: string) => {
    if (!name) return '';
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('');
  };

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <View>
          <Text style={styles.brand}>{currentEntite?.libelle}</Text>
          <Text style={styles.greeting}>
            Bonjour,
          </Text>
          <Text style={styles.greeting}>
            {currentLogin?.nomClassement}
          </Text>
          <Text style={styles.date}>Bienvenue sur votre espace</Text>
        </View>

        <TouchableOpacity style={styles.profileBubble} onPress={toggleModal}>
          <Text style={styles.profileInitials}>
            {getInitials(currentLogin?.nomClassement || '')}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        <TouchableOpacity
          style={styles.primaryCard}
          onPress={() => handlePress({ route: 'NouvelleDemande' })}
          activeOpacity={0.85}
        >
          <View style={styles.primaryIcon}>
            <MaterialCommunityIcons name="plus" size={34} color="#fff" />
          </View>
          <Text style={styles.primaryTitle}>Nouvelle absence</Text>
          <Text style={styles.primarySubtitle}>Créer une demande</Text>
        </TouchableOpacity>

        <Text style={styles.sectionLabel}>Mon Espace</Text>
        <TilesList tiles={shortcuts} onPress={handlePress} />
      </ScrollView>

      <MenuContextuel
        visible={menuVisible}
        navigation={navigation}
        onClose={toggleModal}
      />

      <Dialog
        visible={biometricDialogVisible}
        onClose={dismissBiometricProposal}
        title="Connexion biométrique"
        showCloseButton={false}
        fitContent
      >
        <View style={styles.biometricDialogContent}>
          <MaterialCommunityIcons name="fingerprint" size={48} color="#1D4ED8" />
          <Text style={styles.biometricText}>
            Voulez-vous activer la connexion avec {biometricLabel} pour la prochaine fois ?
          </Text>
          <View style={styles.biometricActions}>
            <Pressable
              onPress={dismissBiometricProposal}
              style={styles.biometricButtonSecondary}
              testID='BT_BIOMETRIC_LATER'
            >
              <Text style={styles.biometricButtonTextSecondary}>Plus tard</Text>
            </Pressable>
            <Pressable
              onPress={handleEnableBiometric}
              disabled={biometricActivating}
              style={styles.biometricButtonPrimary}
              testID='BT_BIOMETRIC_ENABLE'
            >
              <Text style={styles.biometricButtonTextPrimary}>
                {biometricActivating ? 'Activation…' : 'Activer'}
              </Text>
            </Pressable>
          </View>
        </View>
      </Dialog>
    </View>
  )
}
